import { api } from "../client";
import type { UserSummary, UserUpdate } from "../types";

export const userApi = {
  requestCode: (email: string, turnstileToken?: string) =>
    api<{ error: boolean; message: string }>("user/auth/request-code", { body: { email, turnstileToken } }),
  verifyCode: (email: string, code: string) =>
    api<{ error: boolean; guid: string }>("user/auth/verify-code", { body: { email, code } }),
  passwordLogin: (email: string, password: string, turnstileToken?: string) =>
    api<{ error: boolean; guid: string }>("user/auth/login", { body: { email, password, turnstileToken } }),
  setPassword: (password: string) =>
    api<{ error: boolean; message: string }>("user/auth/set-password", { body: { password } }),
  summary: () => api<{ error: boolean; user: UserSummary }>("user/summary").then((r) => r.user),
  update: (patch: UserUpdate) =>
    api<{ error: boolean; message: string; synchUpdated: number }>("user/updateuser", { body: patch }),
  synchUpdate: () => api<{ error: boolean; synchUpdated: number }>("user/synchupdate").then((r) => r.synchUpdated),
  authCheck: () => api<{ error: boolean; message: boolean }>("user/authcheck", { ignoreAuthFailure: true }),
  /**
   * Ensure this browser has a session cookie and report who it belongs to
   * (#289). Called after a Garmin connect (that flow runs on a lambda that
   * cannot mint one), to upgrade a browser that still only has a guid in
   * localStorage, and on boot to restore a session Safari has purged.
   */
  ensureSession: () =>
    api<{ error: boolean; guid: string }>("user/auth/session", { ignoreAuthFailure: true }),
  /** Revoke this browser's session server-side. */
  logoutSession: () =>
    api<{ error: boolean }>("user/auth/logout", { ignoreAuthFailure: true }),
  deleteSelf: () => api<{ error: boolean; message: string }>("user/delete"),
  /**
   * Push target for "new activity" notifications (#385): an FCM/APNs token on
   * the native apps, the browser PushSubscription as JSON on the web.
   */
  registerDevice: (device_token: string, platform: PushPlatform) =>
    api<{ error: boolean; message: string }>("user/registermessagedevice", { body: { device_token, platform } }),
  /** Forget one push target: notifications off, or signing out, on this device. */
  unregisterDevice: (device_token: string, opts: { authKeyOverride?: string } = {}) =>
    api<{ error: boolean; message: string }>("user/unregistermessagedevice", {
      body: { device_token },
      ignoreAuthFailure: true,
      ...opts,
    }),
};

export type PushPlatform = "ANDROID" | "IOS" | "WEB";
